import { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';

// Single sign-on providers shown on the screen. Only Google is enabled in Supabase
// (Authentication > Providers). Microsoft was listed here too, but that provider is
// off, so the button sent people to a raw JSON error page. Add it back here, with its
// logo, in the same change that turns the provider on.
const SSO_PROVIDERS = [{ id: 'google', label: 'Continue with Google' }];

// Matches what the Settings and docs say. Supabase's own minimum is lower; asking for
// more up front means the rule is stated before the first failed try, not after it.
const PASSWORD_MIN = 8;

const C = {
  bg: "#0A1526", surface: "#16233F", border: "#28395C", muted: "#64748b",
  text: "#e2e8f0", textBright: "#f1f5f9", accent: "#00A0C8", danger: "#e53e3e",
};

const css = {
  page:        { minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"Inter,sans-serif", padding:"20px", boxSizing:"border-box" },
  card:        { background:C.surface, border:`1px solid ${C.border}`, borderRadius:"12px", padding:"32px", width:"100%", maxWidth:"360px", boxSizing:"border-box" },
  logo:        { textAlign:"center", marginBottom:"20px" },
  logoImg:     { width:"100px", marginBottom:"12px" },
  logoText:    { fontSize:"24px", fontWeight:"800", color:"#fff" },
  logoAccent:  { color:C.accent },
  tagline:     { fontSize:"12px", color:C.muted, marginTop:"4px" },
  heading:     { fontSize:"16px", fontWeight:"700", color:C.textBright, textAlign:"center", margin:"0 0 18px" },
  label:       { display:"block", fontSize:"11px", color:C.muted, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:"6px" },
  input:       { background:C.bg, border:`1px solid ${C.border}`, borderRadius:"6px", color:C.text, padding:"9px 12px", fontSize:"13px", width:"100%", outline:"none", boxSizing:"border-box" },
  inputLocked: { background:"rgba(0,160,200,0.05)", border:`1px solid rgba(0,160,200,0.3)`, borderRadius:"6px", color:C.muted, padding:"9px 12px", fontSize:"13px", width:"100%", outline:"none", boxSizing:"border-box" },
  field:       { marginBottom:"12px" },
  hint:        { fontSize:"11px", color:C.muted, marginTop:"5px", lineHeight:"1.4" },
  btn:         { padding:"10px 16px", borderRadius:"6px", fontSize:"13px", fontWeight:"600", cursor:"pointer", border:"none", background:C.accent, color:"#fff", width:"100%", marginTop:"4px" },
  btnGhost:    { padding:"10px 16px", borderRadius:"6px", fontSize:"13px", fontWeight:"600", cursor:"pointer", border:`1px solid ${C.border}`, background:"transparent", color:C.text, width:"100%", marginTop:"8px" },
  ssoBtn:      { padding:"10px 16px", borderRadius:"6px", fontSize:"13px", fontWeight:"500", cursor:"pointer", border:`1px solid ${C.border}`, background:"#fff", color:"#374151", width:"100%", marginTop:"8px", display:"flex", alignItems:"center", justifyContent:"center", gap:"10px" },
  linkBtn:     { background:"none", border:"none", padding:0, color:C.accent, cursor:"pointer", fontSize:"inherit", fontFamily:"inherit" },
  toggle:      { textAlign:"center", marginTop:"16px", fontSize:"12px", color:C.muted },
  error:       { color:C.danger, fontSize:"12px", marginBottom:"12px", padding:"8px 10px", background:"rgba(229,62,62,0.1)", borderRadius:"6px", lineHeight:"1.45" },
  success:     { color:"#68d391", fontSize:"12px", marginBottom:"12px", padding:"8px 10px", background:"rgba(104,211,145,0.1)", borderRadius:"6px", lineHeight:"1.45" },
  invite:      { fontSize:"12px", marginBottom:"14px", padding:"10px 12px", background:"rgba(0,160,200,0.08)", border:`1px solid rgba(0,160,200,0.25)`, borderRadius:"6px", color:C.accent },
  divider:     { display:"flex", alignItems:"center", gap:"12px", margin:"16px 0", color:C.muted, fontSize:"12px" },
  divLine:     { flex:1, borderTop:`1px solid ${C.border}` },
  note:        { fontSize:"11px", color:C.muted, marginTop:"16px", textAlign:"center", lineHeight:"1.5" },
  showBtn:     { position:"absolute", right:"8px", top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:C.muted, cursor:"pointer", fontSize:"11px", fontFamily:"inherit", padding:"4px 6px" },
};

const GoogleLogo = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// The invitation token from an emailed link (/accept-invite?token=...), or the legacy /?invite=...
// Read it once, at load, for the same reason as URL_ERROR below: App() stores the token and then
// rewrites the URL to "/" while it renders, which is BEFORE this screen mounts, so reading
// window.location.search inside the effect finds nothing and the invitation lookup never ran for
// emailed links (broken since 2026-08-07, when that rewrite was added; accepting still worked,
// because App.js accepts from the stored token after sign-in). Found by scripts/staging-smoke.mjs's
// logged-out run, 2026-10-28.
const INVITE_TOKEN_FROM_URL = (() => {
  try {
    const p = new URLSearchParams(window.location.search || '');
    return p.get('token') || p.get('invite') || '';
  } catch { return ''; }
})();

// When an emailed link fails (expired, already used) Supabase sends the person back
// with the reason in the URL. Read it once, at load, before anything tidies the URL.
const URL_ERROR = (() => {
  try {
    const raw = (window.location.hash || '').replace(/^#/, '') || (window.location.search || '').replace(/^\?/, '');
    const p = new URLSearchParams(raw);
    const code = p.get('error_code');
    const desc = p.get('error_description');
    if (!code && !desc) return '';
    if (code === 'otp_expired') return 'That email link has expired or was already used. Sign in, or ask for a new one.';
    return desc ? desc.replace(/\+/g, ' ') : 'That email link could not be used.';
  } catch { return ''; }
})();

// Plain-language versions of the errors people actually hit.
function friendlyAuthError(error) {
  const code = error?.code || '';
  const msg  = error?.message || '';
  if (code === 'email_not_confirmed' || /email not confirmed/i.test(msg)) return { kind: 'unconfirmed' };
  if (code === 'invalid_credentials' || /invalid login credentials/i.test(msg)) {
    return { kind: 'text', text: "That email and password don't match. Check them and try again, or reset your password." };
  }
  if (code === 'user_already_exists' || /already registered/i.test(msg)) {
    return { kind: 'text', text: 'There is already an account with that email. Sign in instead, or reset your password if you forgot it.' };
  }
  if (code === 'over_email_send_rate_limit' || /rate limit/i.test(msg)) {
    return { kind: 'text', text: 'Too many emails were requested. Wait a minute and try again.' };
  }
  if (code === 'weak_password') return { kind: 'text', text: `Choose a stronger password (at least ${PASSWORD_MIN} characters).` };
  return { kind: 'text', text: msg || 'Something went wrong. Please try again.' };
}

function Shell({ children }) {
  return (
    <div style={css.page}>
      <div style={css.card}>
        <div style={css.logo}>
          <img src={process.env.PUBLIC_URL + '/timekeep-logo.png'} alt="" style={css.logoImg} />
          <div style={css.logoText}><span style={css.logoAccent}>Time</span>Keep</div>
          <div style={css.tagline}>Time tracking & invoicing</div>
        </div>
        {children}
        <div style={{ ...css.note, marginTop: '20px' }}>
          <a href="/terms" style={{ color: C.muted }}>Terms of Service</a>
          <span aria-hidden="true">{'  ·  '}</span>
          <a href="/privacy" style={{ color: C.muted }}>Privacy Policy</a>
        </div>
      </div>
    </div>
  );
}

function PasswordField({ id, label, value, onChange, autoComplete, hint, placeholder }) {
  const [shown, setShown] = useState(false);
  return (
    <div style={css.field}>
      <label htmlFor={id} style={css.label}>{label}</label>
      <div style={{ position: 'relative' }}>
        <input id={id} name={id} style={{ ...css.input, paddingRight: '52px' }} type={shown ? 'text' : 'password'}
          autoComplete={autoComplete} placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} />
        <button type="button" style={css.showBtn} onClick={() => setShown(s => !s)} aria-pressed={shown}>{shown ? 'Hide' : 'Show'}</button>
      </div>
      {hint && <div style={css.hint}>{hint}</div>}
    </div>
  );
}

export default function Auth() {
  const [invite, setInvite]     = useState(null);
  const [inviteErr, setInviteErr] = useState('');
  const [mode, setMode]         = useState('login');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [name, setName]         = useState('');
  const [loading, setLoading]   = useState(false);
  const [ssoLoading, setSsoLoading] = useState('');
  const [error, setError]       = useState('');
  const [message, setMessage]   = useState('');
  // Set once an account was just created (or a sign-in was refused because the email
  // isn't confirmed yet): the address we sent a confirmation link to.
  const [pendingEmail, setPendingEmail] = useState('');
  const [unconfirmed, setUnconfirmed]   = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [resentTo, setResentTo] = useState('');

  useEffect(() => {
    if (URL_ERROR) {
      setError(URL_ERROR);
      window.history.replaceState({}, '', window.location.pathname);
    }
    // If Auth remounted after a successful signup (happens when email confirmation is
    // disabled and onAuthStateChange fires before the message can render), restore state.
    const justSignedUp = sessionStorage.getItem('signupSuccess');
    if (justSignedUp) {
      sessionStorage.removeItem('signupSuccess');
      setPendingEmail(justSignedUp === '1' ? '' : justSignedUp);
      if (justSignedUp !== '1') setEmail(justSignedUp);
      setMode('confirm');
      return;
    }
    // Support both /accept-invite?token= (new) and /?invite= (legacy); captured at load, see above
    const token = INVITE_TOKEN_FROM_URL;
    if (!token) return;

    supabase.rpc('get_invitation_detail_by_token', { p_token: token }).then(({ data, error }) => {
      if (error || !data?.length) {
        setInviteErr('Invitation not found. The link may be invalid or has already been used.');
        setMode('signup');
        return;
      }
      const inv = data[0];
      switch (inv.invite_status) {
        case 'expired':
          setInviteErr('This invitation has expired. Please contact your admin for a new invite.');
          setMode('signup');
          return;
        case 'accepted':
          setInviteErr('This invitation has already been accepted. Try signing in instead.');
          setMode('login');
          return;
        case 'revoked':
          setInviteErr('This invitation has been revoked. Please contact your admin.');
          setMode('signup');
          return;
        default:
          setInvite({ token, ...inv });
          setEmail(inv.email);
          setMode('signup');
      }
    });
  }, []);

  // Count down the "send it again" button so it can't be hammered.
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(n => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const switchMode = (m) => { setMode(m); setError(''); setMessage(''); setUnconfirmed(false); setPassword(''); };

  const handleLogin = async () => {
    setError(''); setMessage(''); setUnconfirmed(false);
    if (!email.trim() || !password) { setError('Enter your email and password.'); return; }
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    if (error) {
      const f = friendlyAuthError(error);
      if (f.kind === 'unconfirmed') setUnconfirmed(true);
      else setError(f.text);
    }
    setLoading(false);
  };

  const handleSignup = async () => {
    setError('');
    if (!name.trim()) { setError('Please enter your name.'); return; }
    if (!email.trim()) { setError('Please enter your email address.'); return; }
    if (password.length < PASSWORD_MIN) { setError(`Choose a password with at least ${PASSWORD_MIN} characters.`); return; }
    setLoading(true);
    const { data, error } = await supabase.auth.signUp({
      email: email.trim(), password,
      options: { data: { name: name.trim() }, emailRedirectTo: window.location.origin },
    });
    if (error) { setError(friendlyAuthError(error).text); setLoading(false); return; }
    // With email confirmation on, Supabase answers "success" for an address that already
    // has an account but returns no identities for it (and sends no confirmation email).
    // Say so, rather than telling them to wait for an email that is never coming.
    if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      setError(friendlyAuthError({ code: 'user_already_exists' }).text);
      setLoading(false);
      return;
    }
    // Store invite token so app.js can accept it after email confirmation + login
    if (invite?.token) localStorage.setItem('pendingInviteToken', invite.token);
    // Write to sessionStorage before setting state so the message survives if
    // onAuthStateChange fires a session and causes Auth to unmount/remount.
    sessionStorage.setItem('signupSuccess', email.trim());
    setPendingEmail(email.trim());
    setMode('confirm');
    setResendIn(30);
    // If Supabase returned a session immediately (email confirmation disabled),
    // sign out so the user stays on this screen and sees the confirmation message.
    if (data?.session) await supabase.auth.signOut();
    setLoading(false);
  };

  const handleResend = async (to) => {
    const addr = (to || pendingEmail || email).trim();
    if (!addr || resendIn > 0) return;
    setError(''); setMessage('');
    const { error } = await supabase.auth.resend({ type: 'signup', email: addr, options: { emailRedirectTo: window.location.origin } });
    if (error) { setError(friendlyAuthError(error).text); return; }
    setResentTo(addr);
    setResendIn(30);
  };

  const handleForgot = async () => {
    setError(''); setMessage('');
    if (!email.trim()) { setError('Enter the email address you signed up with.'); return; }
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: window.location.origin });
    if (error) setError(friendlyAuthError(error).text);
    else setMessage(`If there is an account for ${email.trim()}, we've sent a link to choose a new password. It can take a few minutes; check your spam folder too.`);
    setLoading(false);
  };

  const handleSSO = async (provider) => {
    setSsoLoading(provider);
    if (invite?.token) localStorage.setItem('pendingInviteToken', invite.token);
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: window.location.origin },
    });
    if (error) setError(error.message);
    setSsoLoading('');
  };

  const seatLabel = { contractor:'Contractor', admin:'Admin', employee_hourly:'Employee (Hourly)', employee_salary:'Employee (Salary)' };
  const inviteRoleLabel = seatLabel[invite?.seat_type] ?? (invite?.role === 'admin' ? 'Admin' : 'Team Member');

  // ── "Check your email" after creating an account ───────────────────────────
  if (mode === 'confirm') {
    const addr = pendingEmail || email;
    return (
      <Shell>
        <h1 style={css.heading}>Check your email</h1>
        <div style={{ ...css.success, color: C.text, background: 'rgba(0,160,200,0.08)' }}>
          We sent a confirmation link to <strong>{addr || 'your email address'}</strong>. Click it to finish creating your account, then come back and sign in. It can take a few minutes, and it may land in your spam folder.
        </div>
        {error && <div role="alert" style={css.error}>{error}</div>}
        {resentTo && !error && <div style={css.success}>Sent again to {resentTo}.</div>}
        <button type="button" style={css.btn} onClick={() => switchMode('login')}>I've confirmed, take me to sign in</button>
        <button type="button" style={{ ...css.btnGhost, opacity: resendIn > 0 ? 0.6 : 1, cursor: resendIn > 0 ? 'default' : 'pointer' }}
          disabled={resendIn > 0 || !addr} onClick={() => handleResend(addr)}>
          {resendIn > 0 ? `Send it again (${resendIn}s)` : 'Send it again'}
        </button>
        <div style={css.toggle}>
          Wrong address? <button type="button" style={css.linkBtn} onClick={() => { setPendingEmail(''); setResentTo(''); switchMode('signup'); }}>Start over</button>
        </div>
      </Shell>
    );
  }

  const isLogin = mode === 'login', isSignup = mode === 'signup', isForgot = mode === 'forgot';
  const onSubmit = (e) => { e.preventDefault(); if (loading) return; (isLogin ? handleLogin : isSignup ? handleSignup : handleForgot)(); };

  return (
    <Shell>
      <h1 style={css.heading}>
        {isLogin ? 'Sign in to TimeKeep' : isSignup ? (invite ? 'Create your account' : 'Create your TimeKeep account') : 'Reset your password'}
      </h1>

      {inviteErr && <div role="alert" style={css.error}>{inviteErr}</div>}
      {invite && !inviteErr && isSignup && (
        <div style={css.invite}>
          You've been invited to join <strong>{invite.org_name}</strong> as a <strong>{inviteRoleLabel}</strong>.
        </div>
      )}

      {error   && <div role="alert" style={css.error}>{error}</div>}
      {message && <div role="status" style={css.success}>{message}</div>}
      {unconfirmed && (
        <div role="alert" style={css.error}>
          Your email address isn't confirmed yet. We sent a link to <strong>{email.trim()}</strong> when you signed up.{' '}
          {resentTo === email.trim()
            ? <span>Sent again.</span>
            : <button type="button" style={{ ...css.linkBtn, textDecoration: 'underline' }} disabled={resendIn > 0} onClick={() => handleResend(email)}>
                {resendIn > 0 ? `Send it again (${resendIn}s)` : 'Send it again'}
              </button>}
        </div>
      )}

      <form onSubmit={onSubmit} noValidate>
        {isSignup && (
          <div style={css.field}>
            <label htmlFor="auth-name" style={css.label}>Your name</label>
            <input id="auth-name" name="name" style={css.input} placeholder="Jane Smith" autoComplete="name" value={name} onChange={e => setName(e.target.value)} />
          </div>
        )}

        <div style={css.field}>
          <label htmlFor="auth-email" style={css.label}>Email</label>
          {invite && isSignup
            ? <input id="auth-email" name="email" style={css.inputLocked} value={email} readOnly />
            : <input id="auth-email" name="email" style={css.input} type="email" placeholder="you@example.com" autoComplete={isLogin ? 'username' : 'email'} value={email} onChange={e => setEmail(e.target.value)} />}
        </div>

        {!isForgot && (
          <PasswordField id="auth-password" label="Password" value={password} onChange={setPassword}
            autoComplete={isLogin ? 'current-password' : 'new-password'}
            hint={isSignup ? `At least ${PASSWORD_MIN} characters.` : null} />
        )}

        {isLogin && (
          <div style={{ textAlign: 'right', margin: '-4px 0 8px', fontSize: '12px' }}>
            <button type="button" style={css.linkBtn} onClick={() => switchMode('forgot')}>Forgot your password?</button>
          </div>
        )}

        <button type="submit" style={css.btn} disabled={loading}>
          {isLogin ? (loading ? 'Signing in...' : 'Sign in') : isSignup ? (loading ? 'Creating account...' : 'Create account') : (loading ? 'Sending...' : 'Send reset link')}
        </button>
      </form>

      {!isForgot && SSO_PROVIDERS.length > 0 && (
        <>
          <div style={css.divider}><div style={css.divLine}/><span>or</span><div style={css.divLine}/></div>
          {SSO_PROVIDERS.map(p => (
            <button key={p.id} type="button" style={css.ssoBtn} onClick={() => handleSSO(p.id)} disabled={!!ssoLoading}>
              <GoogleLogo />{ssoLoading === p.id ? 'Redirecting...' : p.label}
            </button>
          ))}
        </>
      )}

      {isSignup && !invite && (
        <div style={css.note}>
          This creates your own workspace. Joining a team that already uses TimeKeep? Open the invitation link your admin sent you instead, so you're added to their organization.
        </div>
      )}

      {isLogin && (
        <div style={css.toggle}>
          Don't have an account? <button type="button" style={css.linkBtn} onClick={() => switchMode('signup')}>Sign up</button>
        </div>
      )}
      {!isLogin && (
        <div style={css.toggle}>
          {isSignup && invite ? 'Already have an account? ' : 'Back to '}
          <button type="button" style={css.linkBtn} onClick={() => switchMode('login')}>{isSignup && invite ? 'Sign in' : 'sign in'}</button>
        </div>
      )}
    </Shell>
  );
}

// Shown after someone opens the link in a password-reset email. Supabase signs them
// in with a short-lived "recovery" session; without this screen there was nothing
// in the app that let them choose the new password, so the reset never happened.
export function SetNewPassword({ onDone, onCancel }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [done, setDone]         = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < PASSWORD_MIN) { setError(`Choose a password with at least ${PASSWORD_MIN} characters.`); return; }
    if (password !== confirm) { setError("The two passwords don't match."); return; }
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSaving(false);
    if (error) { setError(friendlyAuthError(error).text); return; }
    setDone(true);
  };

  return (
    <Shell>
      <h1 style={css.heading}>Choose a new password</h1>
      {done ? (
        <>
          <div role="status" style={css.success}>Your password has been changed. You're signed in.</div>
          <button type="button" style={css.btn} onClick={onDone}>Continue to TimeKeep</button>
        </>
      ) : (
        <form onSubmit={submit} noValidate>
          {error && <div role="alert" style={css.error}>{error}</div>}
          <PasswordField id="new-password" label="New password" value={password} onChange={setPassword}
            autoComplete="new-password" hint={`At least ${PASSWORD_MIN} characters.`} />
          <PasswordField id="confirm-password" label="Confirm new password" value={confirm} onChange={setConfirm} autoComplete="new-password" />
          <button type="submit" style={css.btn} disabled={saving}>{saving ? 'Saving...' : 'Save new password'}</button>
          <div style={css.toggle}>
            <button type="button" style={css.linkBtn} onClick={onCancel}>Skip for now and sign out</button>
          </div>
        </form>
      )}
    </Shell>
  );
}

// Signed in, but the profile row could not be read or created. Shown instead of the
// login screen, which made it look as though signing in had silently failed.
export function AccountLoadError({ onRetry, onSignOut }) {
  return (
    <Shell>
      <h1 style={css.heading}>We couldn't load your account</h1>
      <div style={{ ...css.note, marginTop: 0, marginBottom: '14px', fontSize: '12px' }}>
        You're signed in, but your profile didn't load. Try again. If it keeps happening, sign out and back in.
      </div>
      <button type="button" style={css.btn} onClick={onRetry}>Try again</button>
      <button type="button" style={css.btnGhost} onClick={onSignOut}>Sign out</button>
    </Shell>
  );
}
